import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, ArrowLeft, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  generateUniqueCode,
  generateSubscriptionDisplayId,
  generateFallbackDisplayId
} from '@/lib/dbUtils';

const Step6Summary = () => {
  const navigate = useNavigate();
  const { 
    state, 
    resetWizard 
  } = useSubscriptionWizard();
  
  useEffect(() => {
    if (!state.selectedCustomer) {
      navigate('../step1', { relative: 'path' });
    }
  }, [state.selectedCustomer, navigate]);

  const totalAmount = state.selectedPlans.reduce((total, plan) => {
    return total + (plan.quantity * plan.unit_price);
  }, 0);

  const handleSubmit = async () => {
    try {
      // Create main subscription using new database structure
      const { data: mainSubscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .insert({
          subscription_type: 'main', // Always create main subscription first
          parent_subscription_id: null,
          state: 'active',
          start_date: new Date(state.startDate).toISOString(),
          next_billing_at: new Date(new Date(state.startDate).setMonth(new Date(state.startDate).getMonth() + 1)).toISOString(),
          po_number: state.poNumber || null,
          payment_source_id: state.paymentSourceId || null,
          net_term_days: state.netTermDays || 0,
          hubspot_opportunity_id: state.selectedOpportunity?.id || null,
          due_date_type: state.dueDateType || 'fixed_day',
          due_date_day: state.dueDateDay || 5,
          due_date_next_month: state.dueDateNextMonth || false,
        })
        .select()
        .single();

      if (subscriptionError) throw subscriptionError;

      // Generate and update the unique display_id using the utility function
      const displayId = await generateUniqueCode({
        tableName: 'subscriptions',
        columnName: 'display_id',
        generateCodeFunction: generateSubscriptionDisplayId,
        fallbackGenerator: generateFallbackDisplayId
      });
      
      const { error: updateDisplayIdError } = await supabase
        .from('subscriptions')
        .update({ display_id: displayId })
        .eq('id', mainSubscription.id);
      
      if (updateDisplayIdError) {
        console.error('Erro ao atualizar display_id:', updateDisplayIdError);
        toast.warning('Aviso: Falha ao gerar ID de exibição único, mas assinatura criada.');
      }

      // Associate main customer as owner
      await supabase
        .from('subscription_customers')
        .insert({
          subscription_id: mainSubscription.id,
          customer_id: state.selectedCustomer!.id,
          customer_role: 'owner'
        });

      if (state.subscriptionType === 'simple') {
        // CENÁRIO 1: Assinatura Simples
        console.log('Creating simple subscription...');
        
        // Create subscription items for main subscription
        const subscriptionItems = state.selectedPlans.map(plan => {
          const safeDate = (dateString) => {
            try {
              const date = new Date(dateString);
              return isNaN(date.getTime()) ? new Date() : date;
            } catch (e) {
              console.warn(`Invalid date: ${dateString}, using current date as fallback`);
              return new Date();
            }
          };

          const startDate = safeDate(plan.current_term_start);
          const endDate = safeDate(plan.current_term_end);
          const nextBillingDate = new Date(startDate);
          nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);

          return {
            subscription_id: mainSubscription.id,
            subscription_plan_id: plan.subscription_plan_id,
            quantity: plan.quantity,
            unit_price: plan.unit_price,
            state: 'active',
            current_term_start: startDate.toISOString(),
            current_term_end: endDate.toISOString(),
            term_start: startDate.toISOString(),
            currency_code: state.currencyCode,
            next_billing_at: nextBillingDate.toISOString(),
            amount_price: plan.quantity * plan.unit_price,
            billing_cycles: plan.billing_cycles,
            billing_by: plan.billing_by || 'by-self'
          };
        });

        const { error: itemsError } = await supabase
          .from('subscription_items')
          .insert(subscriptionItems);

        if (itemsError) throw itemsError;

        // Associate child customers as participants
        if (state.selectedChildCustomers.length > 0) {
          const participantRelations = state.selectedChildCustomers.map(child => ({
            subscription_id: mainSubscription.id,
            customer_id: child.id,
            customer_role: 'participant'
          }));

          const { error: participantsError } = await supabase
            .from('subscription_customers')
            .insert(participantRelations);

          if (participantsError) throw participantsError;
        }

      } else if (state.subscriptionType === 'split') {
        // CENÁRIO 2: Assinatura com Split
        console.log('Creating split subscriptions...');
        
        // Create child subscriptions
        const childSubscriptionPromises = state.childSubscriptions.map(async (childConfig) => {
          // Create child subscription
          const { data: childSubscription, error: childSubError } = await supabase
            .from('subscriptions')
            .insert({
              subscription_type: 'child',
              parent_subscription_id: mainSubscription.id,
              state: 'active',
              start_date: new Date(state.startDate).toISOString(),
              next_billing_at: new Date(new Date(state.startDate).setMonth(new Date(state.startDate).getMonth() + 1)).toISOString(),
              po_number: state.poNumber || null,
              payment_source_id: state.paymentSourceId || null,
              net_term_days: state.netTermDays || 0,
              hubspot_opportunity_id: state.selectedOpportunity?.id || null,
              due_date_type: state.dueDateType || 'fixed_day',
              due_date_day: state.dueDateDay || 5,
              due_date_next_month: state.dueDateNextMonth || false,
            })
            .select()
            .single();

          if (childSubError) throw childSubError;

          // Generate and update the unique display_id for child subscription
          const childDisplayId = await generateUniqueCode({
            tableName: 'subscriptions',
            columnName: 'display_id',
            generateCodeFunction: generateSubscriptionDisplayId,
            fallbackGenerator: generateFallbackDisplayId
          });
          
          const { error: updateChildDisplayIdError } = await supabase
            .from('subscriptions')
            .update({ display_id: childDisplayId })
            .eq('id', childSubscription.id);
          
          if (updateChildDisplayIdError) {
            console.error('Erro ao atualizar display_id da child subscription:', updateChildDisplayIdError);
            toast.warning('Aviso: Falha ao gerar ID de exibição único para uma das assinaturas filhas.');
          }

          // Associate child customer as owner
          await supabase
            .from('subscription_customers')
            .insert({
              subscription_id: childSubscription.id,
              customer_id: childConfig.customer.id,
              customer_role: 'owner'
            });

          // Associate participants
          if (childConfig.participants.length > 0) {
            const participantRelations = childConfig.participants.map(participant => ({
              subscription_id: childSubscription.id,
              customer_id: participant.id,
              customer_role: 'participant'
            }));

            await supabase
              .from('subscription_customers')
              .insert(participantRelations);
          }

          // Create subscription items based on allocations
          const childSubscriptionItems = [];
          for (const [planId, quantity] of Object.entries(childConfig.planAllocations)) {
            if (quantity > 0) {
              const plan = state.selectedPlans.find(p => p.subscription_plan_id === planId);
              if (plan) {
                const safeDate = (dateString) => {
                  try {
                    const date = new Date(dateString);
                    return isNaN(date.getTime()) ? new Date() : date;
                  } catch (e) {
                    return new Date();
                  }
                };

                const startDate = safeDate(plan.current_term_start);
                const endDate = safeDate(plan.current_term_end);
                const nextBillingDate = new Date(startDate);
                nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);

                childSubscriptionItems.push({
                  subscription_id: childSubscription.id,
                  subscription_plan_id: planId,
                  quantity: quantity,
                  unit_price: plan.unit_price,
                  state: 'active',
                  current_term_start: startDate.toISOString(),
                  current_term_end: endDate.toISOString(),
                  term_start: startDate.toISOString(),
                  currency_code: state.currencyCode,
                  next_billing_at: nextBillingDate.toISOString(),
                  amount_price: quantity * plan.unit_price,
                  billing_cycles: plan.billing_cycles,
                  billing_by: plan.billing_by || 'by-self'
                });
              }
            }
          }

          if (childSubscriptionItems.length > 0) {
            const { error: childItemsError } = await supabase
              .from('subscription_items')
              .insert(childSubscriptionItems);

            if (childItemsError) throw childItemsError;
          }

          return childSubscription;
        });

        await Promise.all(childSubscriptionPromises);
      }

      // Create subscription contacts for main subscription
      if (state.selectedContacts.length > 0) {
        const subscriptionContacts = state.selectedContacts.map(contact => ({
          subscription_id: mainSubscription.id,
          contact_id: contact.contact_id,
          contact_type: contact.contact_type
        }));

        const { error: contactsError } = await supabase
          .from('subscription_contacts')
          .insert(subscriptionContacts);

        if (contactsError) throw contactsError;
      }

      // Success message based on subscription type
      let successMessage = 'Assinatura criada com sucesso!';
      if (state.subscriptionType === 'simple' && state.selectedChildCustomers.length > 0) {
        successMessage += ` ${state.selectedChildCustomers.length} participante(s) adicionado(s).`;
      } else if (state.subscriptionType === 'split') {
        successMessage += ` ${state.childSubscriptions.length} assinatura(s) filho criada(s).`;
      }

      toast.success(successMessage);
      
      resetWizard();
      navigate(`/subscription-confirmation/${mainSubscription.id}`);
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(`Erro ao criar assinatura: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  // Adicione esta função para traduzir o billing_start_terms
  const translateBillingStartTerms = (term?: string) => {
    if (!term) return '';
    
    switch (term) {
      case 'immediately':
        return 'Imediatamente';
      case 'custom date':
        return 'Data personalizada';
      default:
        return term;
    }
  };

  // Função para formatar o termo de compromisso
  const formatCommitmentTerm = (periodType?: string, periodDuration?: string | number, frequency?: string) => {
    if (!periodType || !periodDuration || !frequency) return '-';
    
    if (frequency.toLowerCase() === 'one-time') {
      return 'Não se aplica';
    }
    
    const duration = typeof periodDuration === 'string' ? parseInt(periodDuration, 10) : periodDuration;
    
    if (periodType === 'month') {
      return duration === 1 ? '1 mês' : `${duration} meses`;
    } else if (periodType === 'year') {
      return duration === 1 ? '1 ano' : `${duration} anos`;
    } else if (periodType === 'one-time') {
      return 'Pagamento único';
    }
    
    return `${duration} x ${frequency}`;
  };

  // Função para formatar o preço com a frequência de cobrança
  const formatPriceWithFrequency = (price: number, currency: string, frequency: string | undefined) => {
    const formattedPrice = formatCurrency(price, currency);
    
    if (!frequency) return formattedPrice;
    
    const frequencyLower = frequency.toLowerCase();
    
    if (frequencyLower === 'one-time') {
      return `BRL ${formattedPrice.replace(/[^\d,]/g, '')}/único`;
    } else if (frequencyLower === 'monthly') {
      return `BRL ${formattedPrice.replace(/[^\d,]/g, '')}/mês`;
    } else if (frequencyLower === 'yearly') {
      return `BRL ${formattedPrice.replace(/[^\d,]/g, '')}/ano`;
    } else if (frequencyLower === 'quarterly') {
      return `BRL ${formattedPrice.replace(/[^\d,]/g, '')}/trimestre`;
    } else if (frequencyLower === 'semiannually') {
      return `BRL ${formattedPrice.replace(/[^\d,]/g, '')}/semestre`;
    }
    
    return `BRL ${formattedPrice.replace(/[^\d,]/g, '')}/${frequencyLower}`;
  };

  return (
    <SubscriptionWizardLayout
      currentStep={5}
      totalSteps={5}
      title="Nova Assinatura - Resumo"
      description="Revise os detalhes da assinatura antes de finalizar"
      onNext={handleSubmit}
      onPrevious={() => {
        // For simple subscriptions, go back to step5 (plans)
        // For split subscriptions, go back to step5b (split config)
        if (state.subscriptionType === 'split') {
          navigate('../step5b', { relative: 'path' });
        } else {
          navigate('../step5', { relative: 'path' });
        }
      }}
      nextButtonText="Finalizar"
    >
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Informações do Cliente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-muted-foreground">Cliente:</div>
              <div>{state.selectedCustomer?.name}</div>
              
              <div className="text-muted-foreground">Clientes Filho:</div>
              <div>
                {state.selectedChildCustomers.length > 0 ? (
                  state.selectedChildCustomers.map(child => child.name).join(', ')
                ) : (
                  'Nenhum cliente filho selecionado'
                )}
              </div>
              
              {state.selectedOpportunity && (
                <>
                  <div className="text-muted-foreground">Oportunidade HubSpot:</div>
                  <div className="flex items-center">
                    <span>{state.selectedOpportunity.dealname}</span>
                    <Badge className="ml-2" variant="outline">ID: {state.selectedOpportunity.id}</Badge>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contatos</CardTitle>
          </CardHeader>
          <CardContent>
            {state.selectedContacts.length === 0 ? (
              <div className="text-muted-foreground">Nenhum contato selecionado</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tipo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.selectedContacts.map((contact) => (
                    <TableRow key={`${contact.contact_id}-${contact.contact_type}`}>
                      <TableCell>
                        {`${contact.contact.first_name} ${contact.contact.last_name || ''}`.trim()}
                      </TableCell>
                      <TableCell>{contact.contact.email}</TableCell>
                      <TableCell>
                        {contact.contact_type === 'technical' && 'Técnico'}
                        {contact.contact_type === 'administrative' && 'Administrativo'}
                        {contact.contact_type === 'billing' && 'Faturamento'}
                        {contact.contact_type === 'pedagogical' && 'Pedagógico'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detalhes da Assinatura</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-muted-foreground">Data de Início:</div>
              <div>{new Date(state.startDate).toLocaleDateString()}</div>
              
              <div className="text-muted-foreground">Moeda:</div>
              <div>{state.currencyCode}</div>
              
              <div className="text-muted-foreground">Número do Pedido (PO):</div>
              <div>{state.poNumber || '-'}</div>
              
              <div className="text-muted-foreground">Forma de Pagamento:</div>
              <div>
                {state.paymentSourceId === 'boleto' 
                  ? 'Boleto Bancário' 
                  : state.paymentSourceId === 'transferencia' 
                    ? 'Transferência' 
                    : state.paymentSourceId || '-'}
              </div>
              
              <div className="text-muted-foreground">Prazo de Pagamento:</div>
              <div>{state.netTermDays} dias</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Planos</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plano</TableHead>
                  <TableHead>Quantidade</TableHead>
                  <TableHead>Preço Unitário</TableHead>
                  <TableHead>Fidelidade</TableHead>
                  <TableHead>Vigência</TableHead>
                  <TableHead>Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.selectedPlans.map((plan, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                          <span className="font-extrabold">{plan.subscription_plan.name}</span>
                          {plan.subscription_plan.description && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="inline-flex items-center justify-center rounded-full h-4 w-4 text-muted-foreground hover:text-foreground">
                                    <Info className="h-3 w-3" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{plan.subscription_plan.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        {(plan.subscription_plan as any).subscription_plan_families?.name && (
                          <span className="text-xs text-muted-foreground">
                            {(plan.subscription_plan as any).subscription_plan_families?.name}
                          </span>
                        )}
                        {plan.subscription_plan.sku && (
                          <span className="text-xs text-muted-foreground">
                            SKU: {plan.subscription_plan.sku}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{plan.quantity}</TableCell>
                    <TableCell>
                      {formatPriceWithFrequency(plan.unit_price, state.currencyCode, plan.subscription_plan.billing_frequency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default">
                        {formatCommitmentTerm(
                          plan.subscription_plan.billing_period_type,
                          plan.subscription_plan.billing_period_duration,
                          plan.subscription_plan.billing_frequency
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(plan.current_term_start).toLocaleDateString()} a {new Date(plan.current_term_end).toLocaleDateString()}
                      {plan.billing_start_terms && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Início: {translateBillingStartTerms(plan.billing_start_terms)}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        Renovação: {plan.auto_renew ? 'Automática' : 'Período fixo'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Ciclos: {plan.billing_cycles || 'Indefinido'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Cobrança: {plan.billing_by === 'distributor' ? 'Distribuidor' : 'Própria'}
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(plan.quantity * plan.unit_price, state.currencyCode)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <div className="flex justify-end mt-4">
              <div className="bg-muted p-4 rounded-md">
                <div className="text-sm font-extrabold">Total: {formatCurrency(totalAmount, state.currencyCode)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step6Summary; 